import axios from 'axios';

// Use environment variable if set, otherwise use relative path
const API_URL = process.env.REACT_APP_API_URL || '';

// For WebSocket, construct based on current location if not set
const WS_URL = process.env.REACT_APP_WS_URL || 
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;

export const api = axios.create({
  baseURL: API_URL ? `${API_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          // Try to refresh the token
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export type ChatMode = 'chat' | 'workflow' | 'research';

export interface Session {
  session_id: string;
  title: string;
  created_at: string;
  last_activity: string;
  messages: any[];
  uploaded_files: string[];
  generated_scripts: string[];
  mode: ChatMode;
  workflow_context_id?: string;
}

export interface FileUpload {
  session_id: string;
  filename: string;
  file_id: string;
  file_type: string;
  size_bytes: number;
  upload_time: string;
  file_source?: 'user_upload' | 'workflow_output';
  execution_id?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ScriptStepDetails {
  what: string;
  why: string;
  example: string;
}

export interface FinancialDetails {
  calculations?: string;
  data_sources?: string;
  data_outputs?: string;
  aggregations?: string;
  filters?: string;
  transformations?: string;
}

export interface ScriptStep {
  id: string;
  type: string;
  title: string;
  description: string;
  details: ScriptStepDetails;
  financial_details?: FinancialDetails;
  code_lines: [number, number];
  validation_controls?: string;
  error_handling?: string;
}

export interface AccuracySummary {
  key_controls?: string;
  assumptions?: string;
  completeness_checks?: string;
}

export interface ScriptBreakdown {
  summary: string;
  steps: ScriptStep[];
  data_flow: Array<{
    from: string;
    to: string;
    description: string;
  }>;
  accuracy_summary?: AccuracySummary;
}

export interface ChatResponse {
  session_id: string;
  message: string;
  script?: string;
  breakdown?: ScriptBreakdown;
  timestamp: string;
}

export interface Script {
  script_id: string;
  session_id: string;
  code: string;
  language: string;
  created_at: string;
  executed: boolean;
  breakdown?: ScriptBreakdown;
  metadata?: Record<string, any>;
  // Versioning and audit fields
  version?: number;
  parent_script_id?: string | null;
  workflow_context_id?: string | null;
}

export interface QAValidationDetail {
  validation: string;
  status: string;
  checks: Record<string, any>;
  issues: string[];
  message?: string;
}

export interface QAValidationSummary {
  total: number;
  passed: number;
  failed: number;
  errors: number;
}

export interface QAValidationResult {
  summary: QAValidationSummary;
  details: QAValidationDetail[];
  overall_status: string;
  executed_at: number;
}

export interface ExecutionResult {
  script_id: string;
  success: boolean;
  output?: string;
  error?: string;
  result_file_id?: string;
  output_files?: string[];
  output_file_details?: Array<{filename: string; file_id: string}>;
  execution_time: number;
  executed_at: string;
  qa_validation?: QAValidationResult;
  script_version?: number;
  current_script_version?: number;
  input_file_ids?: string[];
  input_file_names?: string[];
}

export interface SessionListItem {
  session_id: string;
  title: string;
  created_at: string;
  last_activity: string;
  message_count: number;
  file_count: number;
}

export interface SessionsListResponse {
  sessions: SessionListItem[];
}

export interface TitleGenerationResponse {
  title: string;
  generated: boolean;
  error?: string;
}

export const sessionApi = {
  create: () => api.post<Session>('/sessions/'),
  get: (sessionId: string) => api.get<Session>(`/sessions/${sessionId}`),
  list: () => api.get<SessionsListResponse>('/sessions/'),
  update: (sessionId: string, data: { title?: string; mode?: ChatMode }) => api.put<Session>(`/sessions/${sessionId}`, data),
  generateTitle: (sessionId: string) => api.post<TitleGenerationResponse>(`/sessions/${sessionId}/generate-title`),
  delete: (sessionId: string) => api.delete(`/sessions/${sessionId}`),
  pin: (sessionId: string, pinned: boolean) => api.post(`/sessions/${sessionId}/pin`, { is_pinned: pinned }),
  protect: (sessionId: string, days: number = 30) => api.post(`/sessions/${sessionId}/protect`, { protect_days: days }),
  rename: (sessionId: string, title: string) => api.put(`/sessions/${sessionId}/rename`, { title }),
  getLatestExecution: (sessionId: string) => api.get<{
    execution: {
      id: string;
      workflow_id: string;
      workflow_name?: string;
      status: string;
      started_at: string;
      completed_at?: string;
      success?: boolean;
      output_files?: any[];
      adapted_script_code?: string;
      adaptation_method?: string;
      intent_prompt?: string;
    } | null;
  }>(`/sessions/${sessionId}/latest-execution`),
  getLatestScriptExecution: (sessionId: string) => api.get<{
    execution: {
      id: string;
      script_id: string;
      success: boolean;
      output?: string;
      error?: string;
      execution_time: number;
      started_at: string;
      completed_at?: string;
      result_file_id?: string;
      output_files?: string[];
      output_file_details?: Array<{filename: string; file_id: string}>;
    } | null;
  }>(`/sessions/${sessionId}/latest-script-execution`),
};

export const fileApi = {
  upload: (file: File, sessionId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('session_id', sessionId);
    return api.post<FileUpload>('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getInfo: (fileId: string) => api.get<FileUpload>(`/files/${fileId}`),
  preview: (fileId: string) => api.get(`/files/${fileId}/preview`),
  download: (fileId: string) => `${API_URL}/api/files/${fileId}/download`,
};

export const chatApi = {
  // Non-streaming endpoint removed - use sendMessageStream instead
  sendMessageStream: async (
    sessionId: string, 
    message: string, 
    context?: any,
    mode?: ChatMode,
    onChunk?: (chunk: string) => void,
    onComplete?: (data: any) => void,
    onError?: (error: string) => void
  ) => {
    try {
      const response = await fetch(`${API_URL}/api/chat/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message,
          context,
          mode,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            if (data.type === 'chunk' && onChunk) {
              onChunk(data.content);
            } else if (data.type === 'typing' && onChunk) {
              // Pass typing events as JSON strings so they can be parsed in the component
              onChunk(JSON.stringify(data));
            } else if (data.type === 'script') {
              // Handle script event - pass to onChunk as special format
              if (onChunk) {
                onChunk(JSON.stringify(data));
              }
            } else if (data.type === 'validation_plan') {
              // Handle validation plan silently - it's stored in backend
              // We don't need to show it to users
              console.log('Validation plan received and stored in backend');
            } else if (data.type === 'complete' && onComplete) {
              onComplete(data);
            } else if (data.type === 'error' && onError) {
              // Handle both data.error and data.message for backward compatibility
              onError(data.error || data.message || 'An unknown error occurred');
            }
          } catch (e) {
            console.error('Failed to parse line:', line, e);
          }
        }
      }
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error.message : 'Unknown error');
      }
      throw error;
    }
  },
  getHistory: (sessionId: string) =>
    api.get<{ messages: ChatMessage[] }>(`/chat/history/${sessionId}`),
  
  // New conversation endpoint for post-QA workflow interactions
  workflowConversation: async (params: {
    session_id: string;
    message: string;
    script_id: string;
    qa_analysis?: string;
    mode?: string;
    context_id?: string;
  }) => {
    const response = await fetch(`${API_URL}/api/workflow/conversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  },
};

export const scriptApi = {
  // Generate endpoint removed - scripts are extracted from chat responses
  save: (sessionId: string, code: string, breakdown?: ScriptBreakdown, validationPlan?: any) =>
    api.post<Script>('/scripts/save', {
      session_id: sessionId,
      code,
      breakdown,
      validation_plan: validationPlan,
    }),
  get: (scriptId: string) => api.get<Script>(`/scripts/${scriptId}`),
  fixIssues: async (scriptId: string, data: { issues: any[], original_request: string }) => {
    const response = await api.post(`/scripts/${scriptId}/fix-issues`, data, {
      responseType: 'stream',
      headers: {
        'Accept': 'text/event-stream',
      }
    });
    
    return streamToAsyncIterator(response.data);
  },
  applyFixes: (scriptId: string, data: { fixes: any[], issues_fixed?: any[] }) =>
    api.post<{
      success: boolean;
      new_script_id: string;
      applied_fixes: number;
      diff_preview: string[];
      script: Script;
    }>(`/scripts/${scriptId}/apply-fixes`, data),
  update: (scriptId: string, data: { code?: string; breakdown?: any }) =>
    api.put<Script>(`/scripts/${scriptId}`, data),
  regenerateBreakdown: (scriptId: string, force: boolean = false) =>
    api.post<{
      script_id: string;
      breakdown: ScriptBreakdown;
      breakdown_metadata: any;
      message: string;
    }>(`/scripts/${scriptId}/breakdown/regenerate?force=${force}`),
  getBreakdownStatus: (scriptId: string) =>
    api.get<{
      script_id: string;
      status: 'pending' | 'generating' | 'completed' | 'failed';
      has_breakdown: boolean;
      is_stale: boolean;
      attempts: number;
      last_error: string | null;
      started_at: string | null;
      completed_at: string | null;
      version: number;
      generated_at: string;
      generation_method: string;
    }>(`/scripts/${scriptId}/breakdown/status`),
  execute: (scriptId: string, sessionId: string) =>
    api.post<ExecutionResult>(`/scripts/${scriptId}/execute`, {
      script_id: scriptId,
      session_id: sessionId,
    }),
  getResults: (scriptId: string) =>
    api.get<ExecutionResult>(`/scripts/${scriptId}/results`),
  listBySession: (sessionId: string) =>
    api.get<Script[]>(`/scripts/session/${sessionId}`),
  
  // Version control methods
  getVersions: (scriptId: string) =>
    api.get<Script[]>(`/scripts/${scriptId}/versions`),
  
  getVersionTree: (scriptId: string) =>
    api.get<any>(`/scripts/${scriptId}/version-tree`),
  
  rollbackToVersion: (scriptId: string, reason?: string) =>
    api.post<{ success: boolean; new_script_id: string; new_version: number; script: Script }>(`/scripts/${scriptId}/rollback`, { reason })
};

// Workflow types
export type WorkflowType = 'draft' | 'final';
export type WorkflowStatus = 'draft' | 'published' | 'archived' | 'pending_approval';
export type UATStatus = 'not_started' | 'in_progress' | 'completed' | 'approved' | 'rejected';

export interface WorkflowFunction {
  id: string;
  code: string;
  name: string;
  description?: string;
  parent_id?: string;
  is_active: boolean;
  is_system: boolean;
  children?: WorkflowFunction[];
}

export interface Workflow {
  id: string;
  session_id: string;
  name: string;
  description?: string;
  type: WorkflowType;
  status: WorkflowStatus;
  version: number;
  function_category_id?: string;
  function_category_name?: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
  has_uat: boolean;
  execution_count: number;
  instruction_summary?: string;
}

export interface WorkflowContent {
  instructions_summary?: string;
  ai_model_used?: string;
  input_file_metadata?: Record<string, any>;
  scripts?: Array<{ id: string; code: string }>;
  script_breakdown?: ScriptBreakdown;
  estimated_runtime_seconds?: number;
  complexity_score?: number;
  tags?: string[];
}

export interface WorkflowUAT {
  id: string;
  test_type: string;
  status: UATStatus;
  test_summary?: string;
  performed_by: string;
  performed_at: string;
  approved_by?: string;
  approved_at?: string;
  file_name?: string;
  file_id?: string;
  file_url?: string;
}

export interface WorkflowDetails {
  workflow: Workflow & {
    function_category?: {
      id: string;
      name: string;
      code: string;
    };
  };
  content?: WorkflowContent;
  uat_records?: WorkflowUAT[];
  draft_state?: {
    session_state: any;
    ui_state: any;
    last_message_id?: string;
    last_script_id?: string;
  };
}

export interface SaveDraftRequest {
  session_id: string;
  name: string;
  description?: string;
  function_category_id?: string;
  ui_state?: any;
  script_breakdown?: any;
  // New fields for self-contained draft storage
  workflow_context_id?: string;
  current_phase?: number;
  column_mappings?: Record<string, any>;
  requirements_analysis?: string;
  qa_results?: any;
}

export interface SaveFinalRequest {
  session_id: string;
  name: string;
  description?: string;
  function_category_id: string;
  script_breakdown?: any;
  // New fields for self-contained final workflow
  workflow_context_id?: string;
  script_code?: string;
  file_metadata?: Array<{ file_id: string; file_name: string; file_type: string; size: number }>;
  requirements_analysis?: string;
  column_mappings?: Record<string, any>;
  qa_analysis?: string;
}

export interface FinalizeWorkflowRequest {
  uat_results: Record<string, any>;
  uat_summary?: string;
}

export interface ExecuteWorkflowRequest {
  session_id: string;
  input_files: Array<{ file_id: string; role: string }>;
  parameters?: Record<string, any>;
}

export const workflowApi = {
  // Save operations
  saveDraft: (data: SaveDraftRequest) => 
    api.post<Workflow>('/workflows/save-draft', data),
  
  saveFinal: (data: SaveFinalRequest) => 
    api.post<{ 
      workflow_id: string; 
      instruction_summary: string; 
      suggested_name: string;
      original_prompt: string;
      requires_uat: boolean 
    }>('/workflows/save-final', data),
  
  // UAT operations
  finalizeWithUAT: (workflowId: string, data: FinalizeWorkflowRequest, uatFile?: File) => {
    const formData = new FormData();
    formData.append('uat_results', JSON.stringify(data.uat_results));
    if (data.uat_summary) {
      formData.append('uat_summary', data.uat_summary);
    }
    if (uatFile) {
      formData.append('uat_file', uatFile);
    }
    
    return api.put<Workflow>(`/workflows/${workflowId}/uat`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // List and retrieve
  list: (params?: {
    session_id?: string;
    type?: WorkflowType;
    status?: WorkflowStatus;
    view_mode?: 'published' | 'drafts' | 'all';
    function_category_id?: string;
    created_by?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }) => 
    api.get<{
      workflows: Workflow[];
      total: number;
      skip: number;
      limit: number;
    }>('/workflows/', { params }),
  
  // Get counts for tab badges
  getCounts: (params?: { created_by?: string }) =>
    api.get<{
      published: number;
      drafts: number;
      total: number;
    }>('/workflows/counts', { params }),
  
  get: (workflowId: string) => 
    api.get<WorkflowDetails>(`/workflows/${workflowId}`),
  
  // Restore and execute
  restore: (workflowId: string) => 
    api.post<{
      workflow_id: string;
      session_id: string;
      session_state: any;
      ui_state: any;
    }>(`/workflows/${workflowId}/restore`),
  
  execute: (workflowId: string, data: ExecuteWorkflowRequest) => 
    api.post<{
      execution_id: string;
      status: string;
      started_at: string;
    }>(`/workflows/${workflowId}/execute`, data),
  
  // Update and delete
  update: (workflowId: string, data: {
    name?: string;
    description?: string;
    function_category_id?: string;
  }) => 
    api.put<Workflow>(`/workflows/${workflowId}`, data),
  
  delete: (workflowId: string) => 
    api.delete(`/workflows/${workflowId}`),
  
  // Function categories
  listFunctions: (includeInactive = false) => 
    api.get<WorkflowFunction[]>('/workflows/functions/', {
      params: { include_inactive: includeInactive }
    }),
  
  createFunction: (data: {
    code: string;
    name: string;
    description?: string;
    parent_id?: string;
  }) => 
    api.post<WorkflowFunction>('/workflows/functions/', data),
  
  // File analysis and adaptation endpoints
  analyzeFile: (fileId: string) => 
    api.post<{
      file_id: string;
      filename: string;
      row_count: number;
      columns: Array<{
        name: string;
        data_type: string;
        sample_values: any[];
        null_count: number;
        unique_count: number;
        total_count: number;
        numeric_stats?: any;
        date_format?: string;
      }>;
      file_format: string;
    }>(`/workflows/analyze-file/${fileId}`),
  
  compareFiles: (data: {
    workflow_id: string;
    new_file_id: string;
  }) => 
    api.post<{
      original_structure: any;
      new_structure: any;
      matched_columns: Array<{
        original_column: string;
        new_column: string;
        confidence: number;
        transformation_needed: boolean;
        transformation_type?: string;
      }>;
      unmatched_original: string[];
      unmatched_new: string[];
      structural_changes: string[];
    }>('/workflows/compare-files', data),
  
  adaptScript: (data: {
    workflow_id: string;
    column_mappings: Array<{
      original_column: string;
      new_column: string;
      confidence: number;
      transformation_needed: boolean;
      transformation_type?: string;
    }>;
  }) => 
    api.post<{
      changes: any[];
      risk_level: string;
      requires_reuat: boolean;
      change_summary: string;
      diff?: string[];
    }>('/workflows/adapt-script', data),
  
  executeAdapted: (workflowId: string, data: {
    session_id: string;
    input_files: Array<{ file_id: string; role: string }>;
    column_mappings?: Array<{
      original_column: string;
      new_column: string;
      confidence: number;
      transformation_needed: boolean;
      transformation_type?: string;
    }>;
    skip_adaptation?: boolean;
    parameters?: any;
  }) => 
    api.post<{
      execution_id: string;
      status: string;
      started_at: string;
      adaptation_applied: boolean;
    }>(`/workflows/${workflowId}/execute-adapted`, data),
  
  // Intent-based adaptation
  adaptByIntent: (workflowId: string, data: {
    session_id: string;
    input_files: Array<{ file_id: string; role: string }>;
    user_intent: string;
  }) =>
    api.post<{
      adaptation_id: string;
      original_intent: string;
      new_intent: string;
      code_preservation_percentage: number;
      changes_preview: Array<{
        line_start: number;
        line_end: number;
        original_code: string;
        adapted_code: string;
        change_type: string;
        reason: string;
        risk_level: string;
      }>;
      risk_assessment: {
        overall_risk: string;
        risk_factors: string[];
        requires_reuat: boolean;
        confidence: number;
        recommendation: string;
      };
      adapted_script: string;
      visual_diff?: any[];
      adapted_script_breakdown?: ScriptBreakdown;
    }>(`/workflows/${workflowId}/adapt-intent`, data),
  
  // Original file metadata
  getOriginalFiles: (workflowId: string) => 
    api.get<{
      files?: Array<{
        filename: string;
        role: string;
        file_id?: string;
        upload_date?: string;
        size_bytes?: number;
        row_count?: number;
        columns?: any[];
        sheets?: any[];
        sample_rows?: any[][];
      }>;
      [key: string]: any; // For old format compatibility
    }>(`/workflows/${workflowId}/original-files`),
  
  downloadTemplate: (workflowId: string, fileRole: string, format: 'csv' | 'xlsx' = 'csv') =>
    api.get(`/workflows/${workflowId}/template/${encodeURIComponent(fileRole)}`, {
      params: { format },
      responseType: 'blob'
    }),

  // Execution details
  getExecutionDetails: (executionId: string) =>
    api.get<{
      execution: {
        id: string;
        workflow_id: string;
        session_id: string;
        status: string;
        started_at: string;
        completed_at?: string;
        duration_seconds?: number;
        executed_by: string;
        success?: boolean;
        error?: string;
        input_files?: any[];
        output_files?: any[];
        execution_parameters?: any;
        results_summary?: any;
        adaptation_method?: string;
        intent_prompt?: string;
        code_preservation_ratio?: number;
        adaptation_changes?: any;
        adapted_script_code?: string;
        adapted_script_breakdown?: ScriptBreakdown;
        created_at: string;
      };
      workflow: {
        id: string;
        name: string;
        description?: string;
        instruction_summary?: string;
        original_script?: any;
        script_breakdown?: ScriptBreakdown;
      };
    }>(`/workflows/executions/${executionId}/details`),
};

// Helper function to convert stream response to async iterator
async function* streamToAsyncIterator(stream: any): AsyncGenerator<string> {
  const reader = stream.getReader ? stream.getReader() : stream;
  const decoder = new TextDecoder();
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim());
      
      for (const line of lines) {
        yield line;
      }
    }
  } finally {
    if (reader.releaseLock) {
      reader.releaseLock();
    }
  }
}

// Workflow context API
export const workflowContextApi = {
  get: (contextId: string) =>
    api.get<{
      context_id: string;
      session_id: string;
      status: string;
      phase: number;
      can_resume: boolean;
      data: {
        requirements_analysis?: string;
        column_mappings?: Record<string, string>;
        qa_analysis?: string;
        script_id?: string;
        [key: string]: any;
      };
      created_at: string;
      expires_at: string;
    }>(`/workflow/contexts/${contextId}`),
  
  list: (params?: { session_id?: string; status?: string }) =>
    api.get<Array<{
      context_id: string;
      session_id: string;
      status: string;
      interrupted_at?: string;
      phase: number;
      can_resume: boolean;
      reason?: string;
      created_at: string;
      expires_at: string;
    }>>('/workflow/contexts/', { params }),
};

export const createWebSocket = (sessionId: string): WebSocket => {
  return new WebSocket(`${WS_URL}/ws/chat/${sessionId}`);
};